import React, { createContext, useContext, useState, useEffect } from "react";
import { apiService } from "../services/api";
import { isWidgetRoute } from "../utils/widgetConfig";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Виджеты (/widget, /chat-widget) не используют авторизацию - не тратим
    // лишний запрос /account/me на каждую встройку виджета на сайте клиента.
    if (isWidgetRoute()) {
      setIsLoading(false);
      return;
    }

    // Сначала пытаемся загрузить пользователя из localStorage
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);

      } catch (error) {
        localStorage.removeItem("user");
      }
    }

    // Затем проверяем авторизацию на сервере
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const userData = await apiService.getMe();

      // Проверяем разные возможные структуры ответа
      const user = userData.body || userData;
      if (user && user.id) {
        setUser(user);
        setIsAuthenticated(true);
        localStorage.setItem("user", JSON.stringify(user));
      } else {
        setIsAuthenticated(false);
        localStorage.removeItem("user");
      }
    } catch (error) {
      setIsAuthenticated(false);
      localStorage.removeItem("user");
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await apiService.login(email, password);

      // Проверяем разные возможные структуры ответа
      const user = response.body || response;
      if (user && user.id) {
        setUser(user);
        setIsAuthenticated(true);
        localStorage.setItem("user", JSON.stringify(user));
        return response;
      } else {
        throw new Error("Invalid response structure");
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem("user");
    }
  };

  const isAdmin = () => {
    return user && user.role === "admin";
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    isAdmin,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
